import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useUnreadCount } from '../../hooks/useUnreadCount';
import { useTasks } from '../../hooks/useTasks';
import api from '../../utils/api';
import { shouldAllowNotification } from '../../constants/notificationPreferences';
import { ReceevitLogoCompact } from '../common/ReceevitLogo';
import {
  LayoutDashboard,
  FileText,
  MessageSquare,
  CreditCard,
  Settings,
  User,
  Bell,
  LogOut,
  Menu,
  X,
  Home,
  Send,
  Download,
  Upload,
  CheckSquare,
  Users,
  MoreHorizontal,
  ChevronRight,
  Lock,
  ArrowRight,
  Link as LinkIcon
} from 'lucide-react';

const MobilePortalLayout = () => {
  const { user, logout, notificationPreferences } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [twoFactorStatus, setTwoFactorStatus] = useState({ loading: true, reconfigure: false, data: null });
  const location = useLocation();
  const navigate = useNavigate();

  const { unreadCount: unreadMessages, refreshUnreadCount } = useUnreadCount();
  const { badgeCount: todoCount } = useTasks();

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await api.get('/notifications');
        if (response.data && Array.isArray(response.data.notifications)) {
          const filteredNotifications = response.data.notifications.filter((notification) =>
            shouldAllowNotification(notificationPreferences, notification.type)
          );
          setNotifications(filteredNotifications);
          const unread = response.data.unreadCount ||
            filteredNotifications.filter(n => !n.isRead && !n.read).length;
          setNotificationCount(unread);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    fetchNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [notificationPreferences]);

  useEffect(() => {
    setNotifications((prev) => {
      const filtered = prev.filter((notification) => shouldAllowNotification(notificationPreferences, notification.type));
      setNotificationCount(filtered.filter((notification) => !notification.isRead && !notification.read).length);
      return filtered;
    });
  }, [notificationPreferences]);

  // Main navigation tabs (bottom bar)
  const mainNavigationTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, href: '/portal/dashboard' },
    { id: 'invoices', label: 'Invoices', icon: FileText, href: '/portal/invoices' },
    { id: 'send', label: 'Send', icon: Send, href: '/portal/send-invoice' },
    { id: 'chat', label: 'Chat', icon: MessageSquare, href: '/portal/chat' }
  ];

  // More menu items
  const moreMenuItems = [
    { id: 'payments', label: 'Payments', icon: CreditCard, href: '/portal/payments', badge: 0 },
    { id: 'todo', label: 'To-Do List', icon: CheckSquare, href: '/portal/todo', badge: todoCount },
    { id: 'contacts', label: 'Contacts', icon: Users, href: '/portal/contacts', badge: 0 },
    { id: 'integrations', label: 'Integrations', icon: LinkIcon, href: '/portal/integrations', badge: 0 },
    { id: 'settings', label: 'Settings', icon: Settings, href: '/portal/settings', badge: 0 }
  ];

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Update active tab based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path === '/portal/dashboard' || path === '/portal') setActiveTab('dashboard');
    else if (path.startsWith('/portal/invoices')) setActiveTab('invoices');
    else if (path.startsWith('/portal/send-invoice')) setActiveTab('send');
    else if (path.startsWith('/portal/chat')) setActiveTab('chat');
    else if (path.startsWith('/portal/payments')) setActiveTab('more');
    else if (path.startsWith('/portal/todo')) setActiveTab('more');
    else if (path.startsWith('/portal/contacts')) setActiveTab('more');
    else if (path.startsWith('/portal/integrations')) setActiveTab('more');
    else if (path.startsWith('/portal/settings')) setActiveTab('more');
  }, [location.pathname]);

  useEffect(() => {
    if (!user || user.role === 'additional_user') {
      setTwoFactorStatus({ loading: false, reconfigure: false, data: null });
      return;
    }

    let cancelled = false;

    const loadTwoFactorStatus = async () => {
      try {
        const response = await api.get('/users/security', { __skipAuthTokenPurge: true });
        if (cancelled) return;
        if (response.data.success) {
          const twoFactor = response.data.data?.twoFactor || {};
          const reconfigureRequired = Boolean(twoFactor.reconfigureRequired);
          const hasCompletedInitialSetup = Boolean(twoFactor.hasCompletedInitialSetup);
          const daysRemaining = typeof twoFactor.daysRemaining === 'number' ? twoFactor.daysRemaining : null;
          const needsSetup = twoFactor.enabled !== true;
          let graceExpired = false;
          if (typeof daysRemaining === 'number') {
            graceExpired = daysRemaining <= 0;
          } else if (hasCompletedInitialSetup) {
            graceExpired = true;
          }
          const requireImmediate = reconfigureRequired || (needsSetup && graceExpired);
          setTwoFactorStatus({
            loading: false,
            reconfigure: requireImmediate,
            data: twoFactor
          });
        } else {
          setTwoFactorStatus({ loading: false, reconfigure: false, data: null });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('MobilePortalLayout: failed to load two-factor status', error);
          setTwoFactorStatus(prev => ({ ...prev, loading: false }));
        }
      }
    };

    loadTwoFactorStatus();
    const interval = setInterval(loadTwoFactorStatus, 30000);
    const listener = () => loadTwoFactorStatus();
    const statusListener = (event) => {
      if (event?.detail && typeof event.detail.reconfigureRequired === 'boolean') {
        setTwoFactorStatus(prev => ({
          ...prev,
          reconfigure: event.detail.reconfigureRequired,
          data: {
            ...prev.data,
            reconfigureRequired: event.detail.reconfigureRequired
          }
        }));
      }
    };
    window.addEventListener('twofactor:updated', listener);
    window.addEventListener('twofactor:mobile-status', statusListener);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('twofactor:updated', listener);
      window.removeEventListener('twofactor:mobile-status', statusListener);
    };
  }, [user?.id]);

  useEffect(() => {
    if (
      !twoFactorStatus.loading &&
      twoFactorStatus.reconfigure &&
      user &&
      user.role !== 'additional_user' &&
      !location.pathname.startsWith('/portal/settings')
    ) {
      localStorage.setItem('settings_active_tab', 'security');
      navigate('/portal/settings', { replace: true });
    }
  }, [twoFactorStatus.loading, twoFactorStatus.reconfigure, navigate, user, location.pathname]);

  useEffect(() => {
    if (twoFactorStatus.reconfigure) {
      setShowNotifications(false);
      setShowMoreMenu(false);
      setShowLogoutModal(false);
    }
  }, [twoFactorStatus.reconfigure]);

  const handleGoToSecurity = () => {
    localStorage.setItem('settings_active_tab', 'security');
    if (!location.pathname.startsWith('/portal/settings')) {
      navigate('/portal/settings');
    }
  };

  const showTwoFactorOverlay =
    !twoFactorStatus.loading &&
    twoFactorStatus.reconfigure &&
    !location.pathname.startsWith('/portal/settings');

  const twoFactorDetails = twoFactorStatus.data || {};

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {showTwoFactorOverlay && (
        <div className="fixed inset-0 z-[9998] bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center px-6 text-center">
          <div className="max-w-sm w-full bg-white border border-indigo-100 shadow-xl rounded-2xl p-6 space-y-4">
            <div className="mx-auto w-14 h-14 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <Lock className="w-7 h-7" />
            </div>
            {(() => {
              const isReconfigure = Boolean(twoFactorDetails?.reconfigureRequired);
              return (
                <>
                  <h2 className="text-lg font-semibold text-indigo-900">
                    {isReconfigure
                      ? 'Reconfigure two-factor authentication to continue'
                      : 'Set up two-factor authentication to continue'}
                  </h2>
                  <p className="text-sm text-indigo-800">
                    {isReconfigure
                      ? 'For your security, you must reconfigure your authenticator app before you can keep using Receevit®.'
                      : 'Your grace period has ended. Finish setting up two-factor authentication now to keep using Receevit®.'}
                  </p>
                </>
              );
            })()}
            {Boolean(twoFactorDetails?.firstVerifiedAt) && (
              <p className="text-xs text-indigo-600">
                Last verified on {new Date(twoFactorDetails.firstVerifiedAt).toLocaleString()}
              </p>
            )}
            <button
              onClick={handleGoToSecurity}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg shadow transition"
            >
              Reconfigure now
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-xs text-indigo-700">
              Navigation is disabled until reconfiguration is complete.
            </p>
          </div>
        </div>
      )}
      {/* Mobile Header */}
      <header className="bg-[#1F11ED] text-white px-4 py-0.5 flex items-center justify-between sticky top-0 z-50">
        <Link to="/portal/dashboard" className="flex items-center">
          <ReceevitLogoCompact size={140} forBlueBackground={true} />
        </Link>
        
        <div className="flex items-center space-x-3">
          {/* Notifications */}
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <Bell className="w-5 h-5" />
            {notificationCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                {notificationCount > 9 ? '9+' : notificationCount}
              </span>
            )}
          </button>

          {/* Settings */}
          <Link
            to="/portal/settings"
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <Settings className="w-5 h-5" />
          </Link>

          {/* User Menu */}
          <div className="relative">
            <button 
              onClick={() => setShowLogoutModal(true)}
              className="flex items-center space-x-2 p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                <User className="w-4 h-4" />
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Notifications Dropdown */}
      {showNotifications && (
        <>
          {/* Backdrop - Click to close */}
          <div 
            className="fixed inset-0 bg-black/20 z-[55]"
            onClick={() => setShowNotifications(false)}
          />
          
          {/* Notification Panel */}
          <div 
            className="absolute top-16 right-4 left-4 bg-white rounded-lg shadow-lg border z-[60] max-h-96 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Actions */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-900">Notifications</h3>
                <button
                  onClick={() => setShowNotifications(false)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              
              {/* Action Buttons */}
              {notifications.length > 0 && (
                <div className="flex items-center gap-3">
                  {notificationCount > 0 && (
                    <button
                      onClick={async () => {
                        try {
                          await api.put('/api/notifications/mark-all-read');
                          const response = await api.get('/notifications');
                          if (response.data && Array.isArray(response.data.notifications)) {
                            setNotifications(response.data.notifications);
                            // Use unreadCount from response
                            setNotificationCount(response.data.unreadCount || 0);
                          }
                        } catch (error) {
                          console.error('Error marking all as read:', error);
                        }
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium py-1 px-2 rounded hover:bg-blue-50 transition-colors"
                    >
                      Mark all as read
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      try {
                        // Delete all notifications one by one
                        for (const notif of notifications) {
                          await api.delete(`/notifications/${notif.id}`);
                        }
                        setNotifications([]);
                        setNotificationCount(0);
                        setShowNotifications(false);
                      } catch (error) {
                        console.error('Error clearing all:', error);
                      }
                    }}
                    className="text-xs text-red-600 hover:text-red-800 font-medium py-1 px-2 rounded hover:bg-red-50 transition-colors"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {/* Notifications List */}
            <div className="overflow-y-auto flex-1">
              {notifications.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification) => (
                    <div 
                      key={notification.id} 
                      className={`p-3 hover:bg-gray-50 transition-colors ${
                        !notification.isRead && !notification.read ? 'bg-blue-50/50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 break-words">{notification.message}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(notification.createdAt || notification.timestamp).toLocaleString()}
                          </p>
                        </div>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await api.delete(`/notifications/${notification.id}`);
                              setNotifications(prev => prev.filter(n => n.id !== notification.id));
                              setNotificationCount(prev => Math.max(0, prev - 1));
                            } catch (error) {
                              console.error('Error deleting notification:', error);
                            }
                          }}
                          className="p-1.5 hover:bg-red-50 rounded text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <p className="text-sm text-gray-500">No notifications</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto pb-20">
        <div className="p-4">
          <Outlet />
        </div>
      </main>

      {/* Bottom Navigation - 5 Tabs (4 main + More) */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40">
        <div className="flex justify-around items-center py-2">
          {mainNavigationTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const badgeCount = tab.id === 'chat' ? unreadMessages : 0;
            const isDisabled = twoFactorStatus.reconfigure && !tab.href.startsWith('/portal/settings');
            
            return (
              <Link
                key={tab.id}
                to={tab.href}
                onClick={(e) => {
                  if (isDisabled) {
                    e.preventDefault();
                    handleGoToSecurity();
                    return;
                  }
                  setActiveTab(tab.id);
                }}
                className={`flex flex-col items-center justify-center py-2 px-3 rounded-lg transition-colors flex-1 ${
                  isActive 
                    ? 'text-[#1F11ED] bg-blue-50' 
                    : 'text-gray-600 hover:text-gray-900'
                } ${isDisabled ? 'opacity-60 pointer-events-none' : ''}`}
              >
                <div className="relative">
                  <Icon className="w-6 h-6 mb-1" />
                  {badgeCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                      {badgeCount > 9 ? '9+' : badgeCount}
                    </span>
                  )}
                </div>
                <span className="text-xs font-medium">{tab.label}</span>
              </Link>
            );
          })}
          
          {/* More Button */}
          <button
            onClick={() => setShowMoreMenu(true)}
            className={`flex flex-col items-center justify-center py-2 px-3 rounded-lg transition-colors flex-1 ${
              activeTab === 'more' 
                ? 'text-[#1F11ED] bg-blue-50' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="relative">
              <MoreHorizontal className="w-6 h-6 mb-1" />
              {todoCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                  {todoCount > 9 ? '9+' : todoCount}
                </span>
              )}
            </div>
            <span className="text-xs font-medium">More</span>
          </button>
        </div>
      </nav>

      {/* More Menu Modal */}
      <AnimatePresence>
        {showMoreMenu && (
          <div 
            className="fixed inset-0 bg-black/50 z-50 flex items-end"
            onClick={() => setShowMoreMenu(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-t-3xl w-full max-h-[70vh] overflow-y-auto"
            >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">More Options</h2>
              <button
                onClick={() => setShowMoreMenu(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-2">
              {moreMenuItems.map((item) => {
                const Icon = item.icon;
                const targetHref =
                  (user?.status === 'inactive' || user?.subscriptionStatus === 'cancelled') && item.id === 'settings'
                    ? '/portal/settings?tab=subscription'
                    : item.href;
                return (
                  <Link
                    key={item.id}
                    to={targetHref}
                    onClick={() => {
                      if ((user?.status === 'inactive' || user?.subscriptionStatus === 'cancelled') && item.id === 'settings') {
                        try {
                          localStorage.setItem('settings_active_tab', 'subscription');
                        } catch (error) {
                          console.debug('Unable to set settings_active_tab in localStorage:', error);
                        }
                      }
                      setActiveTab('more');
                      setShowMoreMenu(false);
                    }}
                    className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-[#1F11ED] rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{item.label}</p>
                        {item.badge > 0 && (
                          <p className="text-xs text-gray-600">{item.badge} pending</p>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </Link>
                );
              })}
            </div>
          </motion.div>
        </div>
        )}
      </AnimatePresence>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-6 max-w-sm w-full">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Logout</h3>
          <p className="text-gray-600 mb-6">Are you sure you want to logout?</p>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowLogoutModal(false)}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleLogout}
              className="flex-1 px-4 py-2 bg-[#1F11ED] text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default MobilePortalLayout;
